import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { ICurrentLesson, IStudentTask } from "../../shared/entities/enrollments";
import { routePathNameChanged } from "../../app/model";
import { ILessonsContract } from "../../lessons/model";
import { IUserAuthContract } from "../../student/auth/model";
import { IEnrollmentsContract } from "../../enrollments/model";
import { ICurrentCourseContract } from "../../courses/ui/current";
import { ITasksContract } from "@features/tasks/model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { LessonSectionType } from "@features/shared/entities/lessons";
import { IRouterContract } from "@features/shared/router";
import { reset } from "patronum";
import { IFlagsmithContract } from "@features/flagsmith/model";
import { $introLessons } from "@features/personal-plan/model";
import { TaskSkipped, LessonPagePresented } from "@features/shared/events";
import { $currentCourseId as $globalCurrentCourseId } from "@features/courses/ui/current";

export const currentLessonChanged = createEvent<ICurrentLesson | null>();
export const currentLessonIdChanged = createEvent<number | null>();
const currentSectionIndexChanged = createEvent();
export const setCurrentSectionIndex = createEvent<number>();
export const currentSectionIndexReturned = createEvent();
const currentPagePracticeSkipped = createEvent();
const playgroundOpened = createEvent<string>();
const playgroundHidden = createEvent();
const lessonFinished = createEvent();
const inlinePromptTried = createEvent<{ sectionIndex: number }>();

export const $currentLesson = createStore<ICurrentLesson | null>(null);
export const $currentLessonId = createStore<number | null>(null);
const $isCurrentLessonIntro = createStore(false);
const $canSkipPractice = createStore<boolean>(false);
const $canGoNext = createStore<boolean>(false);
export const $currentSectionIndex = createStore<number>(0);
// The current lesson is re-derived on every enrollment update (each task check). Same sections,
// same reference: the wall must not take that for a newly revealed lesson and reposition.
export const $currentLessonContentSections = createStore<string[]>([], {
    updateFilter: (next, prev) => next.length !== prev.length || next.some((section, i) => section !== prev[i])
});
export const $currentSection = createStore<string>("");
export const $currentSectionType = createStore<LessonSectionType>("content");
const $lessonSectionTypes = createStore<LessonSectionType[]>([]);
const $lessonVimeoVideoIds = createStore<(string | null)[]>([]);
const $inlinePromptTriedBySection = createStore<Record<number, boolean>>({}).on(
    inlinePromptTried,
    (map, { sectionIndex }) => ({ ...map, [sectionIndex]: true })
);
const $isLastSection = createStore<boolean>(false);

const $activePlayground = createStore<string | null>(null)
    .on(playgroundOpened, (s, p) => p)
    .reset(playgroundHidden);

const $tasksOnPage = createStore<string[]>([]);
const $codeTasksMetaOnPage = createStore<{ id: string; type: string }[]>([]);
const $codeTasksOnPage = $codeTasksMetaOnPage.map((meta) => meta.map((item) => item.id));
const $pageQueryApplied = createStore<boolean>(false);
const $lessonFlowType = createStore<"wall" | "page">("page");

const trackLessonPagePresentedFx = createEffect(
    ({ lessonId, pageId, source }: { lessonId: number; pageId: number; source: "open" | "next" | "skip" }) => {
        LessonPagePresented(lessonId, pageId, source);
    }
);

const $pendingLessonPageSource = createStore<"next" | "skip">("next")
    .on(currentLessonIdChanged, () => "next")
    .on(currentPagePracticeSkipped, () => "skip")
    .on(currentSectionIndexChanged, (state) => (state === "skip" ? "skip" : "next"))
    .on(setCurrentSectionIndex, () => "next")
    .on(trackLessonPagePresentedFx.done, () => "next");

const $lastTrackedLessonPage = createStore<number | null>(null)
    .on(trackLessonPagePresentedFx.done, (_, { params }) => params.pageId)
    .reset(currentLessonIdChanged, routePathNameChanged);

const trackCodeTasksSkippedFx = createEffect(
    ({
        courseId,
        topicId,
        lessonId,
        tasks
    }: {
        courseId: number;
        topicId: number;
        lessonId: number;
        tasks: { id: string; type: string }[];
    }) => {
        tasks.forEach(({ id, type }) => {
            const normalizedType = type.trim().length > 0 ? type.trim() : "unknown";
            TaskSkipped(courseId, topicId, lessonId, id, normalizedType);
        });
    }
);

const readPageFromQueryFx = createEffect<void, number | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const pageStr = params.get("page");
    if (pageStr == null) return null;
    const idx = Number(pageStr);
    return Number.isFinite(idx) ? idx : null;
});

const buildUrlWithPageFx = createEffect<number, string | null>((pageIndex) => {
    if (typeof window === "undefined") return null;

    const isLessonRoute = /^\/course\/\d+\/topic\/\d+\/lesson\/\d+$/.test(window.location.pathname);
    if (!isLessonRoute) return null;

    const params = new URLSearchParams(window.location.search);
    const current = params.get("page");
    if (current !== null && Number(current) === pageIndex) {
        return null;
    }
    params.set("page", String(pageIndex));
    const query = params.toString();
    const url = query.length ? `${window.location.pathname}?${query}` : window.location.pathname;
    return url;
});

sample({
    clock: currentLessonChanged,
    target: $currentLesson
});

sample({
    clock: currentLessonIdChanged,
    target: $currentLessonId
});

sample({
    clock: setCurrentSectionIndex,
    target: $currentSectionIndex
});

sample({
    clock: currentSectionIndexChanged,
    source: {
        currentLessonContentSections: $currentLessonContentSections,
        currentSectionIndex: $currentSectionIndex
    },
    filter: ({ currentLessonContentSections, currentSectionIndex }) =>
        currentLessonContentSections.length > 0 && currentSectionIndex < currentLessonContentSections.length,
    fn: ({ currentLessonContentSections, currentSectionIndex }) => {
        return Math.min(currentSectionIndex + 1, currentLessonContentSections.length);
    },
    target: $currentSectionIndex
});

sample({
    clock: currentSectionIndexReturned,
    source: {
        currentLessonContentSections: $currentLessonContentSections,
        currentSectionIndex: $currentSectionIndex
    },
    filter: ({ currentLessonContentSections }) => currentLessonContentSections.length > 0,
    fn: ({ currentLessonContentSections, currentSectionIndex }) => {
        return Math.max(0, Math.min(currentSectionIndex - 1, currentLessonContentSections.length));
    },
    target: $currentSectionIndex
});

sample({
    clock: currentLessonChanged,
    fn: (currentLesson) => {
        return (currentLesson?.content.split("[========]") ?? []).filter((s) => s.trim().length > 0);
    },
    target: $currentLessonContentSections
});

sample({
    source: { sections: $currentLessonContentSections, index: $currentSectionIndex },
    fn: (s) => s.sections[s.index] ?? "",
    target: $currentSection
});

sample({
    source: $currentSection,
    fn: (s): LessonSectionType => {
        const text = s ?? "";
        if (text.includes("http://tixu.tasks") || text.includes("```tixu-task")) return "tasks";
        if (text.includes("http://tixu.inlineprompt") || text.includes("inlineprompt-generation-config"))
            return "inline-prompt";
        const trimmed = text.trimStart();
        const isVideoAtStart = /^\[[^\]]*\]\(http:\/\/tixu\.videos\.(?:s3|vimeo)[^)]*\)/.test(trimmed);
        if (isVideoAtStart) return "video";
        return "content";
    },
    target: $currentSectionType
});

sample({
    source: $currentLessonContentSections,
    fn: (sections): LessonSectionType[] => {
        const out: LessonSectionType[] = [];
        for (const s of sections) {
            const text = s ?? "";
            if (text.includes("http://tixu.tasks") || text.includes("```tixu-task")) {
                out.push("tasks");
                continue;
            }
            if (text.includes("http://tixu.inlineprompt") || text.includes("inlineprompt-generation-config")) {
                out.push("inline-prompt");
                continue;
            }
            const trimmed = text.trimStart();
            const isVideoAtStart = /^\[[^\]]*\]\(http:\/\/tixu\.videos\.(?:s3|vimeo)[^)]*\)/.test(trimmed);
            out.push(isVideoAtStart ? "video" : "content");
        }
        return out;
    },
    target: $lessonSectionTypes
});

sample({
    source: $currentLessonContentSections,
    fn: (sections): (string | null)[] => {
        const out: (string | null)[] = [];
        const re = /\((?:https?:\/\/)?tixu\.videos\.vimeo[^)]*?\s+"([^"]+)"\)/;
        for (const content of sections) {
            const text = typeof content === "string" ? content : "";
            const m = text.match(re);
            out.push(m ? m[1] || null : null);
        }
        return out;
    },
    target: $lessonVimeoVideoIds
});

sample({
    source: { sections: $currentLessonContentSections, index: $currentSectionIndex },
    fn: (s) => s.index >= Math.max(0, s.sections.length - 1),
    target: $isLastSection
});

sample({
    source: {
        $currentSectionIndex,
        $currentLessonContentSections
    },
    filter: (s) =>
        s.$currentLessonContentSections.length > 0 && s.$currentSectionIndex < s.$currentLessonContentSections.length,
    fn: (s) => {
        const text = s.$currentLessonContentSections[s.$currentSectionIndex] ?? "";
        const ids = Array.from(text.matchAll(/\[([^\]]+)\]\(http:\/\/tixu\.tasks.*?\)/g)).reduce((acc, cur) => {
            const idStr = (cur[1] ?? "").trim();
            if (idStr.length > 0) {
                acc.push(idStr);
            }

            return acc;
        }, [] as string[]);
        return ids;
    },
    target: $tasksOnPage
});

sample({
    source: {
        $currentSectionIndex,
        $currentLessonContentSections
    },
    filter: (s) =>
        s.$currentLessonContentSections.length > 0 && s.$currentSectionIndex < s.$currentLessonContentSections.length,
    fn: (s) => {
        const text = s.$currentLessonContentSections[s.$currentSectionIndex] ?? "";
        const codeBlocks = Array.from(text.matchAll(/```\s*tixu-task\s+([\s\S]*?)```/g)).map((m) => m[1] ?? "");
        const map = new Map<string, string>();
        const extract = (block: string, key: string) => {
            const matcher = new RegExp(`^\\[${key}\\]\\s*([\\s\\S]*?)(?=^\\[[^\\]]+\\]|\\s*$)`, "m");
            const found = block.match(matcher);
            const raw = found ? found[1] : "";
            return String(raw).trim();
        };
        for (const block of codeBlocks) {
            const id = extract(block, "id");
            if (id.length === 0) continue;
            if (!map.has(id)) {
                const type = extract(block, "type");
                map.set(id, type);
            }
        }
        return Array.from(map.entries()).map(([id, type]) => ({ id, type }));
    },
    target: $codeTasksMetaOnPage
});
sample({
    clock: $currentSectionIndex.updates,
    source: $currentLessonContentSections,
    filter: (sections, index) => sections.length === index && index > 0,
    fn: () => undefined,
    target: lessonFinished
});

sample({
    clock: $currentLessonId,
    source: $introLessons,
    fn: (introLessons, lessonId) => {
        return introLessons.some((l) => l.lessonId === lessonId);
    },
    target: $isCurrentLessonIntro
});

const contract = new ModelContract(
    {
        $currentLesson,
        $currentLessonId,
        $currentSectionIndex,
        $currentLessonContentSections,
        $activePlayground,
        $canSkipPractice,
        $canGoNext,
        $currentSection,
        $currentSectionType,
        $lessonSectionTypes,
        $lessonVimeoVideoIds,
        $inlinePromptTriedBySection,
        $isLastSection,
        $lessonFlowType,
        $isCurrentLessonIntro
    },
    {
        currentLessonChanged,
        currentLessonIdChanged,
        currentSectionIndexChanged,
        currentSectionIndexReturned,
        setCurrentSectionIndex,
        currentPagePracticeSkipped,
        playgroundOpened,
        playgroundHidden,
        inlinePromptTried,
        lessonFinished
    },
    {},
    {}
);

export type ICurrentLessonContract = typeof contract;

export class CurrentLessonModule extends ModuleDescription<ICurrentLessonContract> {
    private taskModule: ITasksContract = {} as any;

    constructor(
        public userAuthModule: IUserAuthContract,
        public lessonsModule: ILessonsContract,
        public enrollmentsModule: IEnrollmentsContract,
        public routerModule: IRouterContract,
        public currentCourseModule: ICurrentCourseContract,
        public flagsmithModule: IFlagsmithContract
    ) {
        super();
    }

    setDependency(tasksModule: ITasksContract) {
        this.taskModule = tasksModule;
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const $currentCourseId = $globalCurrentCourseId;
        const $currentTopicId = $currentLesson.map((lesson) => lesson?.topic_id ?? null);

        sample({
            source: { currentLesson: $currentLesson },
            filter: (s) => s.currentLesson !== null,
            fn: () => "wall" as const,
            target: $lessonFlowType
        });

        sample({
            source: {
                lessonId: $currentLessonId,
                lessons: this.lessonsModule.stores.$lessons,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons
            },
            filter: ({ lessonId, lessons }) => {
                return lessonId !== null && lessons.length > 0;
            },
            fn: ({ lessonId, lessons, enrollmentsLessons }) => {
                const lesson = lessons.find((c) => c.id === lessonId);
                if (!lesson) return null;

                const enrollmentLesson = enrollmentsLessons?.find((e) => e.lesson_id === lessonId);
                return enrollmentLesson ? { ...enrollmentLesson, ...lesson } : lesson;
            },
            target: currentLessonChanged
        });

        sample({
            source: { $currentLessonId, $tasksOnPage },
            clock: currentPagePracticeSkipped,
            filter: (s) => s.$currentLessonId != null,
            fn: (s) => ({
                tasksIds: s.$tasksOnPage,
                lessonId: s.$currentLessonId as number
            }),
            target: this.taskModule.events.tasksSkipped
        });

        sample({
            source: {
                lessonId: $currentLessonId,
                courseId: $currentCourseId,
                topicId: $currentTopicId,
                codeTasks: $codeTasksMetaOnPage
            },
            clock: currentPagePracticeSkipped,
            filter: ({ lessonId, courseId, topicId, codeTasks }) =>
                lessonId !== null && courseId !== null && topicId !== null && codeTasks.length > 0,
            fn: ({ lessonId, courseId, topicId, codeTasks }) => ({
                lessonId: lessonId as number,
                courseId: courseId as number,
                topicId: topicId as number,
                tasks: codeTasks
            }),
            target: trackCodeTasksSkippedFx
        });

        sample({
            clock: this.taskModule.events.tasksSkipped,
            target: currentSectionIndexChanged
        });

        sample({
            clock: [routePathNameChanged],
            filter: (url: string) => url.match(/\/lesson\/(\d+)/) !== null,
            fn: (url) => {
                const match = url.match(/\/lesson\/(\d+)/);
                return match ? Number(match[1]) : null;
            },
            target: currentLessonIdChanged
        });

        function completedTasksOnPage(taskIds: string[], tasksEnrollment: IStudentTask[]): boolean {
            return taskIds.every((taskId) => {
                const normId = String(taskId).trim().toLowerCase();
                const task = tasksEnrollment.find((t) => String(t.task_id).trim().toLowerCase() === normId);
                const status = task?.status;
                if (!task) {
                    return false;
                }

                return status === UserEnrollmentStatus.Completed;
            });
        }

        const NO_RETRY_TASK_TYPES = new Set(["yes-no-answer"]);

        function completedTasksOnPageWithLesson(
            taskIds: string[],
            tasksEnrollment: IStudentTask[],
            lessonId: number | null,
            noRetryTaskIds?: Set<string>
        ): boolean {
            const norm = (v: unknown) => String(v).trim().toLowerCase();
            const mapped = taskIds.map((taskId) => {
                const base = norm(taskId);
                const withLesson = lessonId != null ? norm(`${taskId}-${lessonId}`) : null;
                const found = tasksEnrollment.find((t) => {
                    const tid = norm(t.task_id);
                    return tid === base || (withLesson !== null && tid === withLesson);
                });
                const status = found?.status;
                if (status === UserEnrollmentStatus.Completed) return true;
                if (status === UserEnrollmentStatus.Failed && noRetryTaskIds?.has(base)) return true;
                return false;
            });
            const allCompleted = mapped.every(Boolean);
            return allCompleted;
        }

        sample({
            source: {
                $tasksOnPage,
                $codeTasksOnPage,
                $codeTasksMetaOnPage,
                $enrollmentsTasks: this.enrollmentsModule.stores.$enrollmentsTasks,
                $currentLessonId
            },
            fn: (s) => {
                const allIds = Array.from(new Set([...(s.$tasksOnPage || []), ...(s.$codeTasksOnPage || [])]));
                if (allIds.length === 0) {
                    return true;
                }
                const noRetryIds = new Set(
                    (s.$codeTasksMetaOnPage || [])
                        .filter((m) => NO_RETRY_TASK_TYPES.has(m.type))
                        .map((m) => String(m.id).trim().toLowerCase())
                );
                const allCompleted = completedTasksOnPageWithLesson(
                    allIds,
                    s.$enrollmentsTasks,
                    s.$currentLessonId,
                    noRetryIds
                );
                return allCompleted;
            },
            target: $canGoNext
        });

        sample({
            source: {
                $tasksOnPage,
                $codeTasksOnPage,
                $codeTasksMetaOnPage,
                $enrollmentsTasks: this.enrollmentsModule.stores.$enrollmentsTasks,
                $currentLessonId
            },
            fn: (s) => {
                const allIds = Array.from(new Set([...(s.$tasksOnPage || []), ...(s.$codeTasksOnPage || [])]));
                if (allIds.length === 0) {
                    return false;
                }
                const noRetryIds = new Set(
                    (s.$codeTasksMetaOnPage || [])
                        .filter((m) => NO_RETRY_TASK_TYPES.has(m.type))
                        .map((m) => String(m.id).trim().toLowerCase())
                );
                const anyNotCompleted = !completedTasksOnPageWithLesson(
                    allIds,
                    s.$enrollmentsTasks,
                    s.$currentLessonId,
                    noRetryIds
                );
                return anyNotCompleted;
            },
            target: $canSkipPractice
        });

        sample({
            clock: $currentLessonContentSections,
            source: { sections: $currentLessonContentSections, applied: $pageQueryApplied },
            filter: (s) => !s.applied && s.sections.length > 0,
            target: readPageFromQueryFx
        });

        sample({
            clock: readPageFromQueryFx.doneData,
            source: { sections: $currentLessonContentSections, applied: $pageQueryApplied },
            filter: (s, pageIdx) => !s.applied && pageIdx != null && s.sections.length > 0,
            fn: ({ sections }, pageIdx) => {
                const idx = Math.max(0, Math.min(pageIdx as number, sections.length - 1));
                return idx;
            },
            target: setCurrentSectionIndex
        });

        sample({
            clock: readPageFromQueryFx.done,
            fn: () => true,
            target: $pageQueryApplied
        });

        sample({
            clock: $pageQueryApplied.updates,
            source: {
                lesson: $currentLesson,
                index: $currentSectionIndex,
                sections: $currentLessonContentSections
            },
            filter: ({ lesson, index, sections }, applied) =>
                applied && lesson !== null && sections.length > 0 && index < sections.length,
            fn: ({ lesson, index }): { lessonId: number; pageId: number; source: "open" | "next" | "skip" } => ({
                lessonId: lesson!.id,
                pageId: index,
                source: "open"
            }),
            target: trackLessonPagePresentedFx
        });

        sample({
            clock: $currentSectionIndex,
            source: {
                lesson: $currentLesson,
                index: $currentSectionIndex,
                sections: $currentLessonContentSections,
                applied: $pageQueryApplied,
                lastTracked: $lastTrackedLessonPage,
                pendingSource: $pendingLessonPageSource
            },
            filter: ({ lesson, sections, applied, index, lastTracked }) =>
                applied &&
                lesson !== null &&
                sections.length > 0 &&
                index < sections.length &&
                lastTracked !== null &&
                index !== lastTracked,
            fn: ({
                lesson,
                index,
                pendingSource,
                lastTracked
            }): {
                lessonId: number;
                pageId: number;
                source: "open" | "next" | "skip";
            } => {
                const resolvedSource: "open" | "next" | "skip" =
                    lastTracked === null ? "open" : pendingSource === "skip" ? "skip" : "next";

                return {
                    lessonId: lesson!.id,
                    pageId: index,
                    source: resolvedSource
                };
            },
            target: trackLessonPagePresentedFx
        });

        sample({
            clock: $currentSectionIndex,
            target: buildUrlWithPageFx
        });

        sample({
            clock: buildUrlWithPageFx.doneData,
            filter: (url): url is string => Boolean(url),
            target: this.routerModule.events.routerReplaceNoScroll
        });

        reset({
            clock: [routePathNameChanged, this.userAuthModule.events.logoutStudent],
            target: [
                $currentLesson,
                $currentLessonId,
                $currentSectionIndex,
                $currentLessonContentSections,
                $lessonVimeoVideoIds,
                $tasksOnPage,
                $activePlayground,
                $pageQueryApplied,
                $inlinePromptTriedBySection,
                $lastTrackedLessonPage,
                $pendingLessonPageSource
            ]
        });
    }
}
